import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import { toast } from 'react-hot-toast';
import { Amplify } from 'aws-amplify';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import awsconfig from '../../aws-exports';
import { addUser } from '../../redux/actions/addUser';
import { Mail, Lock, Eye, EyeOff, Shield, UserPlus, CheckCircle } from 'lucide-react';

// Mock for demo
const mockAddUser = (userData) => ({ type: 'ADD_USER', userData });

Amplify.configure(awsconfig);

const SignUp = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loadingSignup, setLoadingSignup] = useState(false);
  const [loadingConfirmSignup, setLoadingConfirmSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();

  async function resendConfirmationCode(username) {
    try {
      const response = await resendSignUpCode({ username: username });
      // console.log(`responseresponse = ${response}`);
      setIsConfirmed(true);
    } catch (err) {
      // console.log(err.message);
      if (err.message === 'User is already confirmed.') {
        let errorMsg = `User is already confirmed. You can sign in with this user.`;
        toast.error(errorMsg);
        onClose();
      } else {
        toast.error(err.message);
      }
    }
  }

  const handleSignUp = async (e) => {
    e.preventDefault();
    try {
      setLoadingSignup(true);
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          },
          autoSignIn: true
        },
      });

      setIsConfirmed(true);
      setLoadingSignup(false);
    } catch (error) {
      console.error('Error signing up:', error);
      setIsConfirmed(false);
      setLoadingSignup(false);

      if (error.message === 'An account with the given email already exists.') {
        setLoadingSignup(true);
        await resendConfirmationCode(email);
        setLoadingSignup(false);
      } else {
        toast.error(error.message, {
          style: { zIndex: 9999 },
        });
      }
    }
  };

  const handleConfirmSignUp = async () => {
    try {
      setLoadingConfirmSignup(true);
      const { isSignUpComplete, nextStep } = await confirmSignUp({ 
        username: email, 
        confirmationCode 
      });
      setLoadingConfirmSignup(false);
      
      const userData = { email: email, signup_time: new Date() }
      dispatch(addUser(userData));

      let successMsg = "Your sign up was successful. Please contact Art of Logic tech support (support@artoflogictech.com) to register your email with Analytic services. Please ignore if already registered.";
      toast.success(successMsg, {
        duration: 9000,
      });

      onClose();
    } catch (error) {
      console.error('Error confirming sign up:', error);
      setLoadingConfirmSignup(false);
      toast.error(error.message, {
        style: { zIndex: 9999 }
      });
    }
  };

  return (
    <>
      <style>{`
        .signup-container {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }

        .signup-form-group {
          margin-bottom: 20px;
          position: relative;
        }

        .signup-input-container {
          position: relative;
        }

        .signup-input-field {
          width: 100%;
          height: 48px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 0 16px 0 44px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #fff;
          color: #1f2937;
          font-family: inherit;
        }

        .signup-input-field:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          transform: translateY(-1px);
        }

        .signup-input-field::placeholder {
          color: #9ca3af;
        }

        .signup-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          transition: all 0.3s ease;
          pointer-events: none;
        }

        .signup-input-field:focus + .signup-input-icon {
          color: #667eea;
        }

        .signup-password-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 4px;
          border-radius: 6px;
        }

        .signup-password-toggle:hover {
          color: #667eea;
          background: rgba(102, 126, 234, 0.1);
        }

        .signup-btn {
          width: 100%;
          height: 48px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 20px;
        }

        .signup-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
        }

        .signup-btn:active {
          transform: translateY(0);
        }

        .signup-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .confirmation-container {
          background: #f0f9ff;
          border: 2px solid #0ea5e9;
          border-radius: 16px;
          padding: 24px;
          text-align: center;
        }

        .confirmation-icon {
          margin: 0 auto 16px auto;
          color: #0ea5e9;
        }

        .confirmation-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .confirmation-description {
          color: #6b7280;
          font-size: 14px;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }

        .confirmation-email {
          font-weight: 600;
          color: #0ea5e9;
        }

        .password-requirements {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 12px;
          margin-top: 8px;
        }

        .password-requirements-title {
          font-size: 12px;
          font-weight: 600;
          color: #92400e;
          margin: 0 0 6px 0;
        }

        .password-requirements-list {
          font-size: 11px;
          color: #a16207;
          margin: 0;
          line-height: 1.4;
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 16px 0;
        }

        .success-container {
          background: #ecfdf5;
          border: 2px solid #10b981;
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          margin-bottom: 20px;
        }

        .success-icon {
          margin: 0 auto 16px auto;
          color: #10b981;
        }

        .success-title {
          font-size: 18px;
          font-weight: 600;
          color: #065f46;
          margin: 0 0 8px 0;
        }

        .success-message {
          color: #047857;
          font-size: 14px;
          line-height: 1.5;
          margin: 0;
        }

        @media (max-width: 768px) {
          .signup-input-field {
            height: 44px;
            padding: 0 12px 0 40px;
          }

          .signup-input-icon {
            left: 12px;
          }

          .signup-password-toggle {
            right: 12px;
          }

          .signup-btn {
            height: 44px;
          }

          .confirmation-container {
            padding: 20px;
          }
        }
      `}</style>

      <div className="signup-container">
        {!isConfirmed ? (
          <div>
            <div className="signup-form-group">
              <div className="signup-input-container">
                <input
                  className="signup-input-field"
                  required
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Mail className="signup-input-icon" size={18} />
              </div>
            </div>

            <div className="signup-form-group">
              <div className="signup-input-container">
                <input
                  className="signup-input-field"
                  required
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Lock className="signup-input-icon" size={18} />
                <button
                  type="button"
                  className="signup-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              {password && (
                <div className="password-requirements">
                  <div className="password-requirements-title">Password Requirements:</div>
                  <div className="password-requirements-list">
                    Minimum 8 characters, include uppercase, lowercase, numbers, and special characters
                  </div>
                </div>
              )}
            </div>

            <button
              className="signup-btn"
              onClick={handleSignUp}
              disabled={loadingSignup}
            >
              {loadingSignup ? (
                <BeatLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <UserPlus size={18} />
                  Create Account
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="confirmation-container">
            <Shield size={32} className="confirmation-icon" />
            <h3 className="confirmation-title">Verify Your Email</h3>
            <p className="confirmation-description">
              A confirmation code has been sent to{' '}
              <span className="confirmation-email">{email}</span>
            </p>

            <div className="signup-form-group">
              <div className="signup-input-container">
                <input
                  className="signup-input-field"
                  type="text"
                  placeholder="Enter confirmation code"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                />
                <Shield className="signup-input-icon" size={18} />
              </div>
            </div>

            <button
              className="signup-btn"
              onClick={handleConfirmSignUp}
              disabled={loadingConfirmSignup}
            >
              {loadingConfirmSignup ? (
                <BeatLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <CheckCircle size={18} />
                  Verify Email
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default SignUp;